import { format, formatDistanceToNow } from "date-fns";

import {
  type LivePositionResponse,
  type PositionFullResponse,
  type PositionListItemResponse,
  type PositionPatchRequest,
  type PositionRequest,
  type PositionResponse,
} from "../api";
import {
  buildDesiredStartDateStored,
  DEFAULT_DESIRED_START_DATE_BASE,
  desiredStartDateAsksBestTiming,
  parseDesiredStartDateBase,
} from "./position_desired_start_date";
import {
  isPositionBenefitsSatisfied,
  parseBenefitLines,
} from "./position_benefits";
import { parseFaqs, type faq } from "./position_faq";
import { parseHiringProcessMiddleLines } from "./position_hiring_process";
import {
  parseSalaryRangeAmount,
  parseSalaryRangePeriod,
  salaryRangeAsksCandidateExpectations,
} from "./position_salary_range";
import { Utility } from "./utility";

export {
  ASK_SALARY_EXPECTATIONS_PHRASE,
  buildSalaryRangeStored,
  salaryRangeAsksCandidateExpectations,
} from "./position_salary_range";
export {
  ASK_START_TIMING_PHRASE,
  buildDesiredStartDateStored,
  DEFAULT_DESIRED_START_DATE_BASE,
  desiredStartDateAsksBestTiming,
  parseDesiredStartDateBase,
} from "./position_desired_start_date";
export {
  buildHiringProcessNextStep,
  HIRING_PROCESS_INPUT_PLACEHOLDER,
  HIRING_PROCESS_LABEL_1,
  HIRING_PROCESS_LABEL_2,
  HIRING_PROCESS_LINE_OFFER,
  parseHiringProcessMiddleLines,
} from "./position_hiring_process";
export {
  formatFaqs,
  isPositionFaqOptOut,
  parseFaqs,
  POSITION_FAQ_OPT_OUT_SENTINEL,
  type faq,
} from "./position_faq";
export {
  isPositionBenefitsSatisfied,
  parseBenefitLines,
} from "./position_benefits";

export const DEFAULT_EMPLOYMENT_TYPE = "Full time";
export const DEFAULT_INTERVIEW_LANGUAGE = "multi";

type JobDetailsDefaultFields = Pick<
  PositionFullResponse,
  "employment_type" | "desired_start_date" | "interview_language"
>;

/** Builds a partial update containing only visually blank defaulted Job details fields. */
export const buildMissingJobDetailsDefaultsPatch = (
  position: JobDetailsDefaultFields | undefined,
): PositionPatchRequest => {
  const patch: PositionPatchRequest = {};
  if (!position) return patch;
  if (!position.employment_type?.trim()) {
    patch.employment_type = DEFAULT_EMPLOYMENT_TYPE;
  }
  if (!position.desired_start_date?.trim()) {
    patch.desired_start_date = DEFAULT_DESIRED_START_DATE_BASE;
  }
  if (!position.interview_language?.trim()) {
    patch.interview_language = DEFAULT_INTERVIEW_LANGUAGE;
  }
  return patch;
};

const normalizeString = (s: string): string =>
  s
    .replace(/\u00a0/g, " ")
    .trim()
    .replace(/\s+/g, " ");

type PositionListFields = Pick<
  PositionListItemResponse,
  | "candidate_count"
  | "resume_count"
  | "scheduled_count"
  | "interviewed_count"
  | "recommended_count"
  | "total_candidate_count"
  | "automated_invite"
  | "hiring_pipeline"
>;

export interface Position
  extends PositionFullResponse, Partial<PositionListFields> {
  get_local_time(time_string: string): Date;
}
export interface LivePosition extends LivePositionResponse {
  get_local_time(time_string: string): Date;
}

export abstract class PositionUtility<
  T extends Position | LivePosition,
> extends Utility<T> {
  abstract salary_range: string | null;
  abstract desired_start_date: string | null;
  abstract next_step: string | null;
  abstract compensation_and_benefits: string | null;

  get salary_range_asks_expectations(): boolean {
    return salaryRangeAsksCandidateExpectations(this.salary_range);
  }

  get salary_range_amount(): string | null {
    return parseSalaryRangeAmount(this.salary_range);
  }

  get salary_range_period(): string | null {
    return parseSalaryRangePeriod(this.salary_range);
  }

  get desired_start_date_asks_timing(): boolean {
    return desiredStartDateAsksBestTiming(this.desired_start_date);
  }

  get desired_start_date_base(): string {
    return parseDesiredStartDateBase(this.desired_start_date);
  }

  get hiring_process_middle_lines(): string[] {
    return parseHiringProcessMiddleLines(this.next_step);
  }

  get benefit_lines(): string[] {
    return parseBenefitLines(this.compensation_and_benefits);
  }

  get benefits_satisfied(): boolean {
    return isPositionBenefitsSatisfied(this.compensation_and_benefits);
  }
}

export class Position extends PositionUtility<Position> {
  constructor(init?: Partial<PositionFullResponse & PositionListItemResponse>) {
    super();
    Object.assign(this, init);
  }

  // FAQ lives only on the full position (the live list is FAQ-free), so these getters are on Position, not the shared base.
  get faq_items(): faq[] {
    return parseFaqs(this.frequently_asked_questions ?? "");
  }

  get has_complete_faq_pair(): boolean {
    return this.faq_items.some((item) => item.q.trim() && item.a.trim());
  }

  get local_created_at(): Date | null {
    if (this.created_at === null) return null;
    return this.get_local_time(this.created_at);
  }

  get local_created_at_date_string(): string | null {
    if (this.local_created_at === null) return null;
    return format(this.local_created_at, "MMM d, yyyy");
  }

  get local_updated_at(): Date | null {
    if (this.updated_at === null) return null;
    return this.get_local_time(this.updated_at);
  }

  get local_updated_at_relative_string(): string | null {
    if (this.local_updated_at === null) return null;
    return `Last updated ${formatDistanceToNow(this.local_updated_at, { addSuffix: true })}`;
  }

  get local_status() {
    if (this.published === false) {
      return "Draft";
    }
    return "Ready";
  }

  get normalized_work_location(): string | null {
    if (!this.work_location) return null;
    return normalizeString(this.work_location);
  }

  query(query: string): boolean {
    return (
      this.title?.toLowerCase().includes(query.toLowerCase()) ||
      this.job_code?.toLowerCase().includes(query.toLowerCase()) ||
      false
    );
  }

  toRequest(): PositionRequest {
    return {
      title: this.title ?? null,
      overview: this.overview ?? null,
      required_qualifications: this.required_qualifications ?? null,
      preferred_qualifications: this.preferred_qualifications ?? null,
      resume_qualifications: this.resume_qualifications ?? null,
      work_location: this.work_location ?? null,
      work_arrangement: this.work_arrangement ?? null,
      residency_requirements: this.residency_requirements ?? null,
      work_authorization: this.work_authorization ?? null,
      employment_type: this.employment_type ?? null,
      salary_range: this.salary_range ?? null,
      shift_availability: this.shift_availability ?? null,
      compensation_and_benefits: this.compensation_and_benefits ?? null,
      desired_start_date: this.desired_start_date ?? null,
      next_step: this.next_step ?? null,
      additional_questions: this.additional_questions ?? null,
      interview_language: this.interview_language ?? null,
      published: this.published ?? null,
      archived: this.archived ?? null,
    };
  }
}

export class LivePosition extends PositionUtility<LivePosition> {
  constructor(init?: Partial<LivePositionResponse>) {
    super();
    Object.assign(this, init);
  }
}
